import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  commonTranslations,
  navTranslations,
  authTranslations,
  registerTranslations,
  projectsTranslations,
  interviewTranslations,
  profileTranslations,
  projectDetailTranslations,
  conversationDetailTranslations,
  errorsTranslations,
  interviewGuideTranslations,
  createProjectTranslations,
  conversationalSurveyTranslations,
  liveSupervisionTranslations,
  apiKeysTranslations,
  careerProjectTranslations,
  careerAssessmentTranslations,
  careerCvTranslations,
  homeTranslations
} from './pages';

const pageTranslations = {
  common: commonTranslations,
  nav: navTranslations,
  auth: authTranslations,
  register: registerTranslations,
  projects: projectsTranslations,
  interview: interviewTranslations,
  profile: profileTranslations,
  projectDetail: projectDetailTranslations,
  conversationDetail: conversationDetailTranslations,
  errors: errorsTranslations,
  interviewGuide: interviewGuideTranslations,
  createProject: createProjectTranslations,
  conversationalSurvey: conversationalSurveyTranslations,
  liveSupervision: liveSupervisionTranslations,
  apiKeys: apiKeysTranslations,
  careerProject: careerProjectTranslations,
  careerAssessment: careerAssessmentTranslations,
  careerCv: careerCvTranslations,
  home: homeTranslations
};

/**
 * Custom hook for page-specific translations
 * @param {string} page - The page name (e.g., 'interview', 'projects', etc.)
 * @returns {object} - Translation function and other i18n utilities scoped to the page
 */
export const usePageTranslation = (page) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  // Get page-specific translations
  const pageData = pageTranslations[page] || {};
  const translations = pageData[currentLanguage] || pageData['en'] || {};

  // Get common translations
  const commonData = pageTranslations.common[currentLanguage] || pageTranslations.common['en'] || {};
  const navData = pageTranslations.nav[currentLanguage] || pageTranslations.nav['en'] || {};

  // Create translation functions
  //
  // Signature:
  //   t(key)                      — look up nested key, returns key on miss
  //   t(key, 'fallback string')   — same, but returns the fallback string on miss
  //   t(key, { var: 'value' })    — same, with {{var}} interpolation on the value
  //
  // The string-fallback form matches stock react-i18next, which lets us
  // keep readable English defaults at every t() call site instead of
  // sprinkling raw keys into the UI when a translation is missing.
  // Referentially stable per [page, currentLanguage]. Before this, t/commonT/
  // navT were recreated on EVERY render; any component that put t (or a
  // t-derived callback) in a useEffect/useCallback dependency array re-ran the
  // effect every render, and if that effect called setState it looped forever
  // (the report tab hit exactly this — net::ERR_INSUFFICIENT_RESOURCES from an
  // unbounded GET /report flood). translations/commonData/navData are pure
  // functions of [page, currentLanguage], so memoising on those deps is safe.
  const t = useCallback((key, options) => {
    const keys = key.split('.');
    let value = translations;

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }

    if (value === undefined) {
      // String second arg = fallback. Use it silently — no console noise,
      // since a deliberate fallback is the developer's intent.
      if (typeof options === 'string') {
        return options;
      }
      console.warn(`Translation key "${key}" not found for page "${page}" in language "${currentLanguage}"`);
      return key;
    }

    // Handle interpolation (only when options is an object, not a fallback string)
    if (options && typeof options === 'object' && typeof value === 'string') {
      return value.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
        return options[variable] !== undefined ? options[variable] : match;
      });
    }

    return value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, currentLanguage]);

  const commonT = useCallback((key, options) => {
    const keys = key.split('.');
    let value = commonData;
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }
    
    if (value === undefined) {
      console.warn(`Common translation key "${key}" not found in language "${currentLanguage}"`);
      return key;
    }
    
    // Handle interpolation
    if (options && typeof value === 'string') {
      return value.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
        return options[variable] !== undefined ? options[variable] : match;
      });
    }
    
    return value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage]);

  const navT = useCallback((key, options) => {
    const keys = key.split('.');
    let value = navData;

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }

    if (value === undefined) {
      console.warn(`Nav translation key "${key}" not found in language "${currentLanguage}"`);
      return key;
    }

    // Handle interpolation
    if (options && typeof value === 'string') {
      return value.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
        return options[variable] !== undefined ? options[variable] : match;
      });
    }

    return value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage]);

  return {
    t,                  // Page-specific translations
    commonT,            // Common translations
    navT,               // Navigation translations
    i18n               // i18n instance
  };
};

/**
 * Hook specifically for common translations used across multiple pages
 */
export const useCommonTranslation = () => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  
  // Get common translations
  const commonData = pageTranslations.common[currentLanguage] || pageTranslations.common['en'] || {};
  const navData = pageTranslations.nav[currentLanguage] || pageTranslations.nav['en'] || {};

  const commonT = (key, options) => {
    const keys = key.split('.');
    let value = commonData;
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }
    
    if (value === undefined) {
      console.warn(`Common translation key "${key}" not found in language "${currentLanguage}"`);
      return key;
    }
    
    // Handle interpolation
    if (options && typeof value === 'string') {
      return value.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
        return options[variable] !== undefined ? options[variable] : match;
      });
    }
    
    return value;
  };

  const navT = (key, options) => {
    const keys = key.split('.');
    let value = navData;
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }
    
    if (value === undefined) {
      console.warn(`Nav translation key "${key}" not found in language "${currentLanguage}"`);
      return key;
    }
    
    // Handle interpolation
    if (options && typeof value === 'string') {
      return value.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
        return options[variable] !== undefined ? options[variable] : match;
      });
    }
    
    return value;
  };
  return {
    t: commonT,
    navT,
    i18n
  };
};

/**
 * Hook for accessing i18n object directly (for language switching, etc.)
 */
export const useI18n = () => {
  const { i18n } = useTranslation();
  return { i18n };
};
